/**
 * The product catalogue — one description of NextOpp, rendered three ways.
 *
 * The guided tour, the public /docs pages and the in-app help drawer all read
 * from FEATURES below. They are deliberately NOT three sets of prose: a feature
 * list is a promise, and three copies of a promise drift until two of them are
 * lying. `product-tour.test.js` holds the invariants that keep this honest —
 * every route here exists in the router, every nav anchor exists in NAV_ITEMS,
 * and every tier is a real tier from the billing table.
 *
 * The same reasoning already applies to pricing: see
 * backend/tests/test_tier_purchasability.py, which fails the build when a paid
 * tier advertises something the code cannot back. This file is the in-product
 * half of that rule.
 *
 * WRITING RULES, because this copy is user-facing and the product's whole
 * argument is that it does not overstate:
 *   - `value` says what CHANGES for the reader, not what the screen contains.
 *   - `how` is what they actually click, in order. No aspirational steps.
 *   - `limits` is not optional garnish. Every feature that can disappoint
 *     someone says how, here, before they find out themselves.
 *   - Never describe a feature that is not shipped. There is no "coming soon"
 *     in this file.
 */

/** Tier ranking, mirroring backend/billing.py so "at least this tier" is a
 *  comparison rather than a pile of string equality. */
export const TIER_RANK = { free: 0, student: 1, pro: 2, career_pro: 3, coach: 4 };

export const TIER_LABEL = {
  free: "Free",
  student: "Student",
  pro: "Pro",
  career_pro: "Career Pro",
  coach: "Coach",
};

/** True when `userTier` can use something marked `required`. */
export function tierAllows(userTier, required) {
  const have = TIER_RANK[userTier] ?? 0;
  const need = TIER_RANK[required] ?? 0;
  return have >= need;
}

/**
 * Every feature a signed-in user can reach, in the order the sidebar shows
 * them. `slug` is the /docs URL; `nav` is the data-testid of its sidebar entry
 * (or null for things reached from inside another page).
 */
export const FEATURES = [
  {
    id: "dashboard",
    slug: "dashboard",
    name: "Dashboard",
    route: "/dashboard",
    nav: "nav-dashboard",
    tier: "free",
    summary: "Where you are, and what to do next.",
    value:
      "A job search goes wrong quietly — an application you never followed up, a resume you meant to tailor. The dashboard is the one screen that answers 'what have I got open, and what is waiting on me?'",
    how: [
      "Sign in and you land here.",
      "The counters read from your real pipeline — applications by stage, resumes, saved searches.",
      "Follow-ups that are due surface here rather than waiting for you to remember them.",
    ],
    limits: [
      "It reflects what you have recorded. A job you applied to but never added to NextOpp cannot appear.",
    ],
  },
  {
    id: "job-search",
    slug: "job-search",
    name: "Job Search",
    route: "/search",
    nav: "nav-search",
    tier: "free",
    summary: "Search company job boards directly, not a job board.",
    value:
      "Most job sites show you postings that were re-listed by an aggregator, sometimes weeks late, sometimes gone. NextOpp reads the applicant tracking systems companies actually post to, so a result links to the employer's own posting.",
    how: [
      "Type what you do — 'senior backend engineer' — and where, or leave location empty for remote. Add a company name to narrow to one board.",
      "Refine with the filters: posted date, remote, source, department, country.",
      "Open a result to read it, then 'Add to jobs' to start working it.",
    ],
    limits: [
      "Coverage is company boards, not the whole internet. A company that posts only to LinkedIn will not appear.",
      "A posting can be filled before the board removes it. NextOpp shows what the board still publishes.",
      "The sponsorship stance on a result is read from the posting's own words, and the sentence is shown. 'Not stated' means the posting did not say — not that the employer will sponsor.",
    ],
  },
  {
    id: "job-radar",
    slug: "job-radar",
    name: "Job Radar",
    route: "/radar",
    nav: "nav-radar",
    tier: "free",
    summary: "Save a search; get told when something new matches.",
    value:
      "Being early matters more than being thorough. Radar re-runs your searches in the background and emails you what is new, so you stop re-typing the same query every morning.",
    how: [
      "Build a search you like on the Job Search page.",
      "Save it as a Radar search and pick how often it runs.",
      "New matches arrive by email as a digest; open one to go straight to the posting.",
    ],
    limits: [
      "How many searches you can save and how often they run depends on your plan — Free is 1 search, daily. Pro and Career Pro raise both.",
      "Radar finds new postings on boards NextOpp already covers. It does not widen coverage.",
    ],
  },
  {
    id: "jobs",
    slug: "jobs",
    name: "Jobs",
    route: "/jobs",
    nav: "nav-jobs",
    tier: "free",
    summary: "One page per application, with the AI work attached to it.",
    value:
      "This is where an interesting posting becomes an actual application. Everything you generate for a job — match analysis, a tailored resume, a cover letter, company research, interview questions — lives on that job, not scattered across chat windows.",
    how: [
      "Add a job from search, from a URL, or by pasting the description.",
      "Run the ATS match to see how your resume reads against that posting.",
      "Generate what you need, review it, and edit anything you disagree with.",
    ],
    limits: [
      "AI generations count against your monthly quota. The remaining count is shown before you spend one.",
      "Everything generated is a draft for you to review. NextOpp never submits an application for you.",
    ],
  },
  {
    id: "ats-match",
    slug: "ats-match",
    name: "ATS match analysis",
    route: "/jobs",
    nav: null,
    tier: "free",
    summary: "How your resume reads against one specific posting.",
    value:
      "A resume is not good or bad in the abstract — it is aligned or misaligned with the job in front of it. The match analysis names the specific gaps so you can fix the ones that matter.",
    how: [
      "Open a job and choose ATS match.",
      "Pick which of your resumes to score.",
      "Read the missing keywords and the score breakdown, then decide what is worth changing.",
      "After tailoring, score the tailored version for this job — 'Re-score with this version' on the Tailor tab — to see what it changed. Only this job's version can be scored here; a resume tailored for another job is not offered.",
    ],
    limits: [
      "The score is NextOpp's reading of the posting, not the employer's actual system. Treat it as a checklist, not a verdict.",
      "It will not invent experience you do not have — a gap it reports is a real gap.",
    ],
  },
  {
    id: "resumes",
    slug: "resumes",
    name: "Resumes",
    route: "/resumes",
    nav: "nav-resumes",
    tier: "free",
    summary: "Upload once, tailor per job, keep every version.",
    value:
      "Tailoring works, but only if it stays truthful and you can find what you sent. Your library holds the master; each tailored version stays attached to the job it was made for.",
    how: [
      "Upload a resume — PDF or DOCX — and NextOpp parses it into editable sections.",
      "Edit in the editor, or tailor it to a specific job from that job's page.",
      "Drag a role or a bullet by its grip to reorder it, or use the arrows beside it.",
      "Bold a number inside a bullet with ⌘B. It carries into the PDF and the DOCX.",
      "If the page runs a few lines long, tighten the text size, line spacing or margins beneath the preview.",
      "Export to PDF when you are ready to send it.",
    ],
    limits: [
      "Tailoring rewrites emphasis and wording. It does not add employers, titles or dates you did not enter.",
      "Typography settings are remembered in your browser, not saved with the resume.",
      "Premium templates and version history need Career Pro.",
    ],
  },
  {
    id: "tracker",
    slug: "pipeline",
    name: "Pipeline",
    route: "/tracker",
    nav: "nav-tracker",
    tier: "free",
    summary: "Every application, by stage, on one board.",
    value:
      "Ten applications are a list. Forty are a mess. The board shows the whole search at once so you can see where things are stalling instead of guessing.",
    how: [
      "Applications appear here as you add jobs.",
      "Drag a card between stages as things move — applied, interviewing, offer, closed.",
      "Open a card to get back to that job's documents and notes.",
    ],
    limits: [
      "Stages move when you move them. NextOpp does not read your email to detect a rejection.",
    ],
  },
  {
    id: "stars",
    slug: "star-bank",
    name: "STAR Bank",
    route: "/stars",
    nav: "nav-stars",
    tier: "free",
    summary: "Your stories, written once, reused in every interview.",
    value:
      "Interviews ask the same handful of behavioural questions. Writing your strongest examples down once — Situation, Task, Action, Result — means you stop improvising the same story badly at 9am.",
    how: [
      "Add a story: what happened, what you did, what changed because of it.",
      "Tag it so you can find it by the competency it demonstrates.",
      "Pull it up while preparing for a specific interview.",
    ],
    limits: [
      "These are your words. NextOpp will help you structure a story but will not fabricate one.",
    ],
  },
  {
    id: "ranked-jobs",
    slug: "ranked-jobs",
    name: "Ranked jobs",
    route: "/jobs",
    nav: null,
    tier: "career_pro",
    summary: "Your matches, ordered by fit rather than date.",
    value:
      "When a search returns two hundred results, recency is a poor way to choose. Ranking scores each against your profile so the ones worth your afternoon are at the top.",
    how: [
      "Set your target roles, skills and location in Profile — ranking reads from it.",
      "Open the ranked view from your jobs list.",
      "Work down from the top; the score shows why each one placed where it did.",
    ],
    limits: [
      "Ranking is only as good as your profile. A thin profile produces a weak ordering.",
    ],
  },
  {
    id: "outreach",
    slug: "outreach",
    name: "Recruiter outreach drafts",
    route: "/jobs",
    nav: null,
    tier: "career_pro",
    summary: "A first message that refers to the actual job.",
    value:
      "Cold outreach fails when it is obviously a template. These drafts are built from the specific posting and your specific background, so there is something real to react to.",
    how: [
      "Open a job and choose outreach.",
      "Review the draft — it is a starting point, and it should sound like you.",
      "Send it yourself, from your own account.",
    ],
    limits: [
      "NextOpp drafts the message. It does not find contact details, and it does not send anything on your behalf.",
    ],
  },
  {
    id: "salary-prep",
    slug: "salary-prep",
    name: "Salary negotiation prep",
    route: "/jobs",
    nav: null,
    tier: "career_pro",
    summary: "What to say when the number comes up.",
    value:
      "The offer conversation is short and most people are unprepared for it. This gives you a position, the reasoning behind it, and language for the awkward parts.",
    how: [
      "Open a job that has reached offer stage.",
      "Generate the prep and read the reasoning, not just the number.",
      "Adjust it to what you are actually willing to accept.",
    ],
    limits: [
      "Guidance is based on the posting and your profile, not live market data feeds. Sanity-check any figure against your own research.",
    ],
  },
  {
    id: "version-history",
    slug: "resume-versions",
    name: "Resume version history",
    route: "/resumes",
    nav: null,
    tier: "career_pro",
    summary: "See what changed, and go back.",
    value:
      "Tailoring is lossy if you cannot compare. History keeps every saved version so you can see what a rewrite actually changed and restore the one that was working.",
    how: [
      "Open a resume and choose version history.",
      "Compare versions to see what moved.",
      "Restore one if the newer edit went the wrong way.",
    ],
    limits: ["History starts when you first save. It cannot recover edits made before that."],
  },
  {
    id: "mcp",
    slug: "mcp",
    name: "AI assistant access (MCP)",
    route: "/profile",
    nav: null,
    tier: "free",
    summary: "Drive your job search from Claude, Cursor or any MCP client.",
    value:
      "The work happens where you already are. Connect NextOpp to an AI assistant and you can ask it to search boards, add a job, score your resume against it and draft a cover letter — without switching tabs and clicking through four screens.",
    how: [
      "Open Profile → Access Tokens and create a token. It is shown once, so copy it then.",
      "Install the connector: the one-click Desktop Extension for Claude Desktop, or add the server to your client's MCP config by hand.",
      "Paste the token when asked, then just ask — 'find me remote platform engineering roles and score my resume against the best three'.",
    ],
    limits: [
      "It exposes reading, searching and AI drafting only. There is deliberately no submit, no delete, no billing and no admin — an AI client is not an exception to the rule that you send your own applications.",
      "AI tools spend the same monthly quota as the web app. A 402 means your tier limit was reached, not that something is broken.",
      "A token is a password. The Desktop Extension stores it in your OS keychain, and you can revoke it from the same Profile page at any time.",
    ],
    guide: {
      intro:
        "Two ways in, and nothing to download for either. The server runs at nextopp.io, so Claude Code, Cursor and the rest need a URL and your token. Claude Desktop gets the one-click Desktop Extension instead, because it cannot yet send a token to a remote server.",
      steps: [
        {
          title: "Claude Desktop — one click",
          body: "Download the extension and drag it onto Claude Desktop → Settings → Extensions, then paste your token into the form. The token field is marked sensitive, so it lands in your OS keychain rather than a config file on disk.",
        },
        {
          title: "Claude Code",
          body: "Run this once. It writes the server into your MCP config, and every project you open then has the tools. Nothing is installed; the server is ours, at nextopp.io.",
          code: 'claude mcp add --transport http nextopp https://nextopp.io/mcp \\\n  --header "Authorization: Bearer ac_pat_your_token"',
        },
        {
          title: "Cursor, Windsurf, Zed and others",
          body: "Add the server to your client's MCP configuration file. The shape is the same everywhere; only the file location differs. Some clients call the field serverUrl rather than url — check your client's MCP docs if it does not connect.",
          code: '{\n  "mcpServers": {\n    "nextopp": {\n      "url": "https://nextopp.io/mcp",\n      "headers": {\n        "Authorization": "Bearer ac_pat_your_token"\n      }\n    }\n  }\n}',
        },
        {
          title: "Running your own NextOpp?",
          body: "The same server ships in the repository under mcp-server/. The production compose file runs it as the mcp service and proxies it at /mcp on your host, so the configs above work with your hostname in place of nextopp.io. It also runs locally over stdio, with NEXTOPP_PAT and NEXTOPP_API_URL in its environment; the README there has the details.",
        },
        {
          title: "Check it worked",
          body: "Ask your assistant for your dashboard stats. Real pipeline numbers mean the token and URL are both right. A 401 means the token was revoked or mistyped — create a new one on the Profile page.",
        },
      ],
    },
    // Things to say once connected. Every `tools` entry is a real @mcp.tool in
    // mcp-server/server.py, and every tool on the server appears in at least
    // one prompt — both enforced in product-tour.test.js, so the list is the
    // surface demonstrated rather than a sample that drifts.
    prompts: [
      {
        title: "Start here",
        items: [
          {
            say: "Show me my NextOpp dashboard stats.",
            tools: ["dashboard_stats"],
            note: "Real pipeline numbers mean the URL and the token are both right. A message about a missing token means the header did not arrive.",
          },
          { say: "List the resumes I have in NextOpp.", tools: ["list_resumes"] },
          {
            say: "Open my most recent resume and tell me what it leads with.",
            tools: ["get_resume"],
          },
        ],
      },
      {
        title: "Finding jobs",
        items: [
          {
            say: "Search NextOpp for remote platform engineering jobs posted this week and show me the top ten.",
            tools: ["search_jobs"],
          },
          {
            say: "Scan Stripe's careers board and tell me which roles mention Kubernetes.",
            tools: ["scan_company"],
          },
          {
            say: "Search the wider web for staff data engineer roles in Austin.",
            tools: ["search_jobs_web"],
          },
          {
            say: "Save those results as 'Austin data eng, September' so I can come back to them.",
            tools: ["save_search_results"],
          },
          {
            say: "What searches have I saved? Reload the Austin one and tell me if anything is new.",
            tools: ["list_saved_search_results", "load_saved_search_results", "refresh_saved_search_results"],
          },
        ],
      },
      {
        title: "Your pipeline",
        items: [
          { say: "Import the second result into my pipeline.", tools: ["import_job"] },
          {
            say: "Add a job manually: Senior Backend Engineer at Acme, https://acme.com/jobs/123, remote, 180 to 210k.",
            tools: ["add_job"],
          },
          {
            say: "List everything in my pipeline that is still at the Saved stage.",
            tools: ["list_jobs"],
          },
          { say: "Show me the full description of the Acme job.", tools: ["get_job"] },
          {
            say: "Move the Acme job to Applied.",
            tools: ["update_job_status"],
            note: "It moves a card. Nothing is submitted, by design.",
          },
        ],
      },
      {
        title: "AI drafting",
        note: "Each of these spends one AI action from your monthly quota, the same as doing it in the app. A 402 in the result is the tier limit, not a fault.",
        items: [
          {
            say: "Run an ATS match between my latest resume and the Acme job and tell me the three biggest gaps.",
            then: "Show me that match again.",
            tools: ["analyze_ats_match", "get_ats_match"],
          },
          {
            say: "Tailor my resume for the Acme job and summarise what you changed and why.",
            then: "Pull up the tailored version.",
            tools: ["tailor_resume", "get_tailored_resume"],
          },
          {
            say: "Draft a cover letter for the Acme job, direct and unfussy, under 250 words.",
            then: "Read me the cover letter you drafted.",
            tools: ["generate_cover_letter", "get_cover_letter"],
          },
          {
            say: "Research Acme before the interview: product, funding, recent news, likely engineering challenges.",
            then: "Show me the Acme research again.",
            tools: ["generate_company_research", "get_company_research"],
          },
          {
            say: "Give me eight likely interview questions for the Acme role, half behavioural, half system design.",
            then: "Bring back those interview questions.",
            tools: ["generate_interview_questions", "get_interview_questions"],
          },
          {
            say: "Pull my STAR stories and tell me which ones fit the Acme behavioural questions best.",
            tools: ["list_star_stories"],
          },
        ],
      },
      {
        title: "One that chains it",
        items: [
          {
            say: "Find three remote senior Python roles, import the best one, run an ATS match against my current resume, and draft a cover letter. Show me the gaps before you draft.",
            tools: ["search_jobs", "import_job", "list_resumes", "analyze_ats_match", "generate_cover_letter"],
            note: "Five tools in sequence, and a pause where your judgement belongs.",
          },
        ],
      },
    ],
  },
  {
    id: "sponsorship",
    slug: "sponsorship",
    name: "Sponsorship & H-1B history",
    route: "/search",
    nav: null,
    tier: "free",
    summary: "What a posting says about sponsorship, and what the employer has actually filed.",
    value:
      "Postings almost never promise sponsorship, but many refuse it — and finding that out after an evening of tailoring is the expensive way. NextOpp reads each posting's stance from its own words and shows the sentence, and beside it the employer's H-1B filing history from the U.S. Department of Labor's public records.",
    how: [
      "Set your work authorization on your Profile. The layer switches on by itself when your status needs sponsorship; you can turn it on or off there any time.",
      "On Job Search, each result shows its stance — No sponsorship, Sponsorship available, Citizenship or clearance required — with the sentence it came from as the tooltip. Tick 'Hide roles that refuse sponsorship' to drop the refusals.",
      "On a job's page, the employer's certified labor condition applications by fiscal year, with median offered wage and typical titles. A saved Radar search can skip refusals too.",
    ],
    limits: [
      "'Not stated' means the posting did not say. It never means the employer will sponsor.",
      "Certified labor condition applications are the step before an H-1B petition, not visas issued; employers file more than they hire. An employer with none on record may still sponsor.",
      "The filing history is matched on the employer's exact name and imported quarterly from DOL, so it can lag by a quarter and miss a company that files under a different legal name.",
    ],
  },
  {
    id: "profile",
    slug: "profile",
    name: "Profile & account",
    route: "/profile",
    nav: "nav-profile",
    tier: "free",
    summary: "What NextOpp knows about your search.",
    value:
      "Target roles, locations, salary expectations and skills feed ranking, tailoring and Radar. Filling this in properly is the single cheapest thing you can do to make the rest work better.",
    how: [
      "Set target roles, locations and remote preference.",
      "Add your skills — these are what tailoring and matching compare against.",
      "Manage access tokens here if you use the API or the browser extension.",
    ],
    limits: [
      "Changing your profile affects future generations. It does not retroactively rewrite documents you already made.",
    ],
  },
];

/** Look one up by id. Returns undefined for an unknown id — callers decide. */
export function featureById(id) {
  return FEATURES.find((f) => f.id === id);
}

export function featureBySlug(slug) {
  return FEATURES.find((f) => f.slug === slug);
}

/**
 * The guided tour: the SPINE, not the catalogue.
 *
 * Nine steps, chosen because they are the shortest path from "signed in" to
 * "I have applied to something with a resume that fits". The long tail is in
 * /docs, where someone looking for it will actually read it — a tour that
 * visits every feature is a tour people quit in the middle of.
 *
 * `anchor` is a CSS selector resolved at run time. When it does not match
 * anything the step still renders, centred and without a spotlight, so a step
 * can never wedge the tour on a page whose markup moved. TourOverlay tests
 * cover that path specifically.
 */
export const TOUR_STEPS = [
  {
    id: "welcome",
    route: "/dashboard",
    anchor: null,
    title: "Two minutes, and you'll know your way around",
    body:
      "NextOpp finds jobs on company boards, helps you tailor a truthful resume for each one, and keeps track of what you sent. You review everything — nothing is submitted for you.",
  },
  {
    id: "search",
    route: "/dashboard",
    anchor: '[data-testid="nav-search"]',
    feature: "job-search",
    title: "Start where the jobs actually are",
    body:
      "Job Search reads the applicant tracking systems companies post to, so results link to the employer's own posting rather than a re-listing.",
  },
  {
    id: "sponsorship",
    route: "/dashboard",
    anchor: '[data-testid="nav-profile"]',
    feature: "sponsorship",
    title: "If you need sponsorship, say so once",
    body:
      "Set your work authorization on your Profile and every result shows the posting's sponsorship stance, in its own words, with the employer's H-1B filings from public records beside it. Off by default for everyone else; on or off any time from here.",
  },
  {
    id: "radar",
    route: "/dashboard",
    anchor: '[data-testid="nav-radar"]',
    feature: "job-radar",
    title: "Then stop repeating yourself",
    body:
      "Save a search as Radar and it re-runs in the background, emailing you only what is new. Free covers one daily search; paid plans raise both numbers.",
  },
  {
    id: "jobs",
    route: "/dashboard",
    anchor: '[data-testid="nav-jobs"]',
    feature: "jobs",
    title: "A posting becomes an application here",
    body:
      "Each job gets its own page. Match analysis, a tailored resume, a cover letter, company research and interview questions all attach to that job instead of scattering.",
  },
  {
    id: "resumes",
    route: "/dashboard",
    anchor: '[data-testid="nav-resumes"]',
    feature: "resumes",
    title: "One master resume, many tailored versions",
    body:
      "Upload once. Tailoring rewrites emphasis and wording for a specific posting — it will not add a job title or a date you never entered.",
  },
  {
    id: "tracker",
    route: "/dashboard",
    anchor: '[data-testid="nav-tracker"]',
    feature: "tracker",
    title: "See the whole search at once",
    body:
      "The pipeline board shows every application by stage, so you can spot where things are stalling instead of guessing.",
  },
  {
    id: "stars",
    route: "/dashboard",
    anchor: '[data-testid="nav-stars"]',
    feature: "stars",
    title: "Write your stories down once",
    body:
      "Interviews reuse the same behavioural questions. The STAR Bank keeps your strongest examples ready so you are not improvising them at 9am.",
  },
  {
    id: "profile",
    route: "/dashboard",
    anchor: '[data-testid="nav-profile"]',
    feature: "profile",
    title: "The cheapest thing you can do",
    body:
      "Target roles, locations and skills feed matching, ranking and Radar. A filled-in profile makes everything downstream noticeably better.",
  },
  {
    id: "finish",
    route: "/dashboard",
    anchor: null,
    title: "That's the shape of it",
    body:
      "Full documentation for every feature — including what each one will not do — lives in the docs. You can restart this tour any time from the sidebar.",
    docsCta: true,
  },
];

/** localStorage key. Versioned: changing the tour should re-offer it. */
export const TOUR_STORAGE_KEY = "nextopp.tour.v1";

/**
 * Page tours.
 *
 * The product tour above is the spine: ten steps, all on the dashboard,
 * pointing at the sidebar. It says what each area is FOR and never shows a
 * page's own controls. These do: one tour per main page, each step lighting
 * one component of that page in the order it is used.
 *
 * Anchors are the page's own `data-testid`s (or an `id`, or a prefix match
 * `[data-testid^="radar-run-"]` for rows keyed by record). page-tours.test.js
 * checks each one against the markup as text, because an unmatched anchor
 * degrades to a centred card instead of failing — silently.
 *
 * `route` may carry a parameter (`/jobs/:jobId`). `feature` is the catalogue
 * entry whose docs page renders the same walkthrough, so the tour can be read
 * before signing in and the two cannot drift.
 */
export const PAGE_TOURS = [
  {
    id: "dashboard",
    route: "/dashboard",
    feature: "dashboard",
    steps: [
      {
        id: "matches",
        anchor: '[data-testid="dashboard-best-matches"]',
        title: "Your strongest matches, first",
        body: "Every job you have analysed is ranked here by match score. It is the shortlist: the roles worth a tailored resume before the rest.",
      },
      {
        id: "activity",
        anchor: '[data-testid="dashboard-recent-activity"]',
        title: "What changed lately",
        body: "Jobs added, stages moved, drafts generated — a running log of the search, so a week away does not mean starting from memory.",
      },
      {
        id: "followups",
        anchor: '[data-testid="dashboard-followups"]',
        title: "Follow-ups that have come due",
        body: "Set a follow-up date on any job and it surfaces here on the day. Nothing is sent for you; this is the reminder, and the note is yours to write.",
      },
      {
        id: "distribution",
        anchor: '[data-testid="dashboard-score-distribution"]',
        title: "How the whole pipeline scores",
        body: "Strong, medium, low and unscored, as counts. A pipeline that is mostly low is a targeting problem, not a resume problem — worth knowing before tailoring anything.",
      },
      {
        id: "usage",
        anchor: '[data-testid="dashboard-billing-card"]',
        title: "The AI budget this month",
        body: "Analyses, tailoring, letters, research and questions draw from one monthly pool. The count here is what is left; the plan sets the size.",
      },
    ],
  },
  {
    id: "search",
    route: "/search",
    feature: "job-search",
    steps: [
      {
        id: "panel",
        anchor: '[data-testid="job-search-panel"]',
        title: "Five ways to look",
        body: "The tabs are five sources, not five views of one list. Start with the index: it is the fastest and the widest.",
      },
      {
        id: "index",
        anchor: '[data-testid="tab-multi-scan"]',
        title: "Search every company board at once",
        body: "The index holds the open postings from tens of thousands of company boards, refreshed by the background crawler every few hours. Search it like a job site, except each result links to the employer's own posting — and it is free.",
      },
      {
        id: "company",
        // The criteria as a whole: the company is one bracket of the
        // sentence (or one field of the form), and which is shown depends
        // on the door, so the step points at the line that holds them.
        anchor: '[data-testid="search-criteria-row"]',
        title: "One company? Type its name",
        body: "The index already holds every board, so a single company is a filter, not a separate tool. Refresh its board from any result card when you need the last few hours.",
      },
      {
        id: "add",
        anchor: '[data-testid="add-company-toggle"]',
        title: "A board we don't have yet",
        body: "Ask for it — a short form with the careers page address. We check it and add it, and you get an email when it is in.",
      },
      {
        id: "web",
        anchor: '[data-testid="tab-web-search"]',
        title: "The web, and the way into Radar",
        body: "Aggregators cover what company boards do not. A search from this tab can be saved to Radar, which re-runs it in the background and emails you what is new.",
      },
      {
        id: "saved",
        anchor: '[data-testid="saved-results-btn"]',
        title: "Results you kept",
        body: "Any result set can be saved as a named snapshot. Come back to it later, or reload it from an AI assistant over MCP.",
      },
      {
        id: "refine",
        anchor: '[data-testid="results-refine-bar"]',
        title: "Then narrow it down",
        body: "Once results are in, the refine bar filters by source, remote, posted date, location and department. With the sponsorship layer on, it can also hide postings that say they will not sponsor.",
      },
    ],
  },
  {
    id: "radar",
    route: "/radar",
    feature: "job-radar",
    steps: [
      {
        id: "intro",
        anchor: null,
        title: "Searches that keep running",
        body: "Radar is a saved search with a schedule. It re-runs on its own, remembers what it has already shown you, and emails only the new postings. Searches are created from the Search page — 'Save to Radar'.",
      },
      {
        id: "card",
        anchor: '[data-testid^="radar-search-"]',
        title: "One card per saved search",
        body: "The name, how often it runs, and how many postings it has seen so far. Open the query to change what it looks for without making a new one.",
      },
      {
        id: "active",
        anchor: '[data-testid^="radar-toggle-"]',
        title: "Pause it, keep it",
        body: "A paused search stays with its history. Pause when a role type is not what you want this month; resume later and it picks up where it left off.",
      },
      {
        id: "refusals",
        anchor: '[data-testid^="radar-skip-refusals-"]',
        title: "Skip roles that refuse sponsorship",
        body: "With this on, the digest leaves out postings whose own text says no sponsorship, or citizenship required. Postings that say nothing are kept — silence is not a yes.",
      },
      {
        id: "run",
        anchor: '[data-testid^="radar-run-"]',
        title: "Run it now",
        body: "Do not wait for the schedule. A manual run counts against the same daily allowance as a scheduled one.",
      },
      {
        id: "matches",
        anchor: '[data-testid^="radar-view-"]',
        title: "Then work the matches",
        body: "Open the matches, read a posting on the employer's site, and import the ones worth pursuing straight into your jobs.",
      },
    ],
  },
  {
    id: "jobs",
    route: "/jobs",
    feature: "jobs",
    steps: [
      {
        id: "add",
        anchor: '[data-testid="jobs-add-btn"]',
        title: "Any posting can become a job",
        body: "Paste a posting's text or URL. Search results and Radar matches arrive here too. A job is the unit everything else attaches to.",
      },
      {
        id: "filter",
        anchor: '[data-testid="jobs-search-input"]',
        title: "Find one fast",
        body: "Filters by company, role and location as you type. It searches what you have saved, not the web — that is the Search page.",
      },
      {
        id: "sort",
        anchor: '[data-testid="jobs-sort"]',
        title: "Recent, or best fit",
        body: "Recent is the order you added them. Best fit ranks by match score against your profile, with the weights adjustable on paid plans.",
      },
      {
        id: "list",
        anchor: '[data-testid="jobs-list"]',
        title: "Open one to work it",
        body: "Each row is a job's page: analysis, tailored resume, letter, research and interview prep, all kept together with the posting.",
      },
    ],
  },
  {
    id: "job-detail",
    route: "/jobs/:jobId",
    feature: "jobs",
    steps: [
      {
        id: "meta",
        anchor: '[data-testid="job-detail-meta"]',
        title: "The posting's facts",
        body: "Company, role, location, salary if stated, and a link to the employer's own posting. With the sponsorship layer on, the posting's stance appears here in its own words, with the employer's H-1B filings beside it.",
      },
      {
        id: "resume",
        anchor: '[data-testid="job-detail-resume-select"]',
        title: "Which resume this job is measured against",
        body: "Pick the base resume for this job. Analysis, tailoring and the letter all start from it; change it and re-run to compare.",
      },
      {
        id: "status",
        anchor: '[data-testid="job-detail-status-select"]',
        title: "Stage and follow-up",
        body: "Move the job through saved, applied, interviewing and so on, and set a follow-up date. The tracker board and the dashboard read the same field.",
      },
      {
        id: "ats",
        anchor: '[data-testid="tab-ats"]',
        title: "Match analysis",
        body: "Scores the resume against the posting: matched, missing and weak keywords, seniority fit, and what to change. Once a tailored version exists you can score that instead, for this job only.",
      },
      {
        id: "tailor",
        anchor: '[data-testid="tab-tailor"]',
        title: "Tailor, then check it",
        body: "Rewrites emphasis and wording for this posting, and shows every change beside the original. It will not add a title, employer or date you never entered. Re-score to see what the rewrite bought.",
      },
      {
        id: "letter",
        anchor: '[data-testid="tab-letter"]',
        title: "A cover letter in your voice",
        body: "Drafted from your resume and the posting, in the tone you choose. It is a draft: read it, change it, then download it.",
      },
      {
        id: "research",
        anchor: '[data-testid="tab-research"]',
        title: "Research and interview prep",
        body: "Company research for the conversation, and likely interview questions for this role with STAR-shaped answers built from your own stories.",
      },
      {
        id: "apply",
        anchor: '[data-testid="tab-apply"]',
        title: "Applying stays yours",
        body: "The assistant fills forms with what you have already written and stops before Submit. NextOpp never sends an application for you.",
      },
    ],
  },
  {
    id: "resumes",
    route: "/resumes",
    feature: "resumes",
    steps: [
      {
        id: "upload",
        anchor: '[data-testid="resume-upload-btn"]',
        title: "Upload once",
        body: "PDF or DOCX. It is parsed into sections you can edit; the parse is checked for anything it could not read, and says so.",
      },
      {
        id: "cards",
        anchor: '[data-testid^="resume-card-"]',
        title: "Base resumes and tailored versions",
        body: "A base resume is a master. A tailored version is tied to the job it was made for, and is marked as such — it is not offered for other jobs.",
      },
      {
        id: "edit",
        anchor: '[data-testid^="resume-edit-"]',
        title: "Edit the sections",
        body: "Change any section in place. Version history keeps the earlier text on paid plans, so an edit is never a loss.",
      },
      {
        id: "export",
        anchor: '[data-testid^="resume-download-pdf-"]',
        title: "Export from one template",
        body: "PDF and DOCX come from the same stylesheet, so the two files match. What you download is what the parser read plus your edits — nothing invented.",
      },
    ],
  },
  {
    id: "tracker",
    route: "/tracker",
    feature: "tracker",
    steps: [
      {
        id: "board",
        anchor: '[data-testid="kanban-board"]',
        title: "The whole search on one board",
        body: "Every job, in a column for its stage. Where the cards pile up is where the search is stalling.",
      },
      {
        id: "columns",
        anchor: '[data-testid^="kanban-column-"]',
        title: "Stages, with counts",
        body: "Saved, applied, interviewing, offer, and the rest. The count in each header is the number to watch week over week.",
      },
      {
        id: "cards",
        anchor: '[data-testid^="kanban-card-"]',
        title: "Move a card to change its stage",
        body: "Drag it, or use the move control for keyboard and touch. Either way it is the same status field the job page shows.",
      },
    ],
  },
  {
    id: "stars",
    route: "/stars",
    feature: "stars",
    steps: [
      {
        id: "generate",
        anchor: '[data-testid="star-generate-btn"]',
        title: "Start from your resume",
        body: "Pick a resume and generate: each story is drawn from a real bullet — situation, task, action, result — for you to correct and sharpen.",
      },
      {
        id: "manual",
        anchor: '[data-testid="star-add-manual"]',
        title: "Or write one yourself",
        body: "The best stories are often not on the resume. Add them here and tag them so an interview question can find them.",
      },
      {
        id: "tags",
        anchor: '[data-testid="star-tag-filter"]',
        title: "Filter by what a question asks for",
        body: "Leadership, conflict, failure, scale. Tags are how a story is found when the interview prep for a job suggests one.",
      },
      {
        id: "stories",
        anchor: '[data-testid="star-stories"]',
        title: "Keep them sharp",
        body: "Edit any story in place. A story with a measurable result is worth three with a vague one.",
      },
    ],
  },
  {
    id: "profile",
    route: "/profile",
    feature: "profile",
    steps: [
      {
        id: "preferences",
        anchor: "#profile-preferences",
        title: "What you are looking for",
        body: "Target roles, locations, remote preference, experience, salary, industries and skills. Matching, ranking and Radar all read from here — the single cheapest improvement to everything downstream.",
      },
      {
        id: "work-auth",
        anchor: '[data-testid="profile-work-auth-status"]',
        title: "Work authorization",
        body: "If you will need sponsorship, say so here. The job page will then warn when a posting's own text refuses it.",
      },
      {
        id: "sponsorship",
        anchor: '[data-testid="profile-show-sponsorship"]',
        title: "The sponsorship layer, on or off",
        body: "Stance chips on results, the refusal filter, the H-1B card and the warning are one switch. On by default only for a status that needs sponsorship; change it any time.",
      },
      {
        id: "links",
        anchor: "#profile-links",
        title: "Your links",
        body: "LinkedIn, GitHub and portfolio go into cover letters and the application assistant, so they are typed once.",
      },
      {
        id: "save",
        anchor: '[data-testid="profile-save-btn"]',
        title: "Save, and it takes effect everywhere",
        body: "Rankings and Radar use the new profile from the next run. Nothing is re-sent or re-submitted on your behalf.",
      },
    ],
  },
];

/** localStorage key for which page tours have been taken: a JSON array of ids. */
export const PAGE_TOUR_STORAGE_KEY = "nextopp.tour.pages.v1";

function routeMatches(pattern, pathname) {
  const want = pattern.split("/").filter(Boolean);
  const have = pathname.split("/").filter(Boolean);
  if (want.length !== have.length) return false;
  return want.every((seg, i) => seg.startsWith(":") || seg === have[i]);
}

/** The page tour for a pathname, or null. Parameters match any one segment. */
export function pageTourForPath(pathname) {
  return PAGE_TOURS.find((t) => routeMatches(t.route, pathname || "")) || null;
}

/** The tours a feature's docs page walks through, in catalogue order. */
export function pageToursForFeature(featureId) {
  return PAGE_TOURS.filter((t) => t.feature === featureId);
}

/**
 * The tour's first two steps in the words of the user's door. Everything
 * after them is the same product; only the opening should say who it is for.
 * Keyed by step id, so a step that moves or is renamed simply keeps its
 * professional copy rather than breaking the tour.
 */
export const MODE_TOUR_COPY = {
  early: {
    welcome: {
      title: "Two minutes, and you'll know your way around",
      body:
        "NextOpp finds first jobs, internships and seasonal work on the boards employers actually post to, helps you write a truthful first resume, and keeps track of what you sent. You review everything — nothing is submitted for you.",
    },
    search: {
      title: "Start where the jobs actually are",
      body:
        "Job Search opens on roles that say they take beginners — entry level, internships, apprenticeships, seasonal — and, if you told us your age, on postings open to it. Results link to the employer's own page.",
    },
  },
  government: {
    welcome: {
      title: "Two minutes, and you'll know your way around",
      body:
        "NextOpp reads federal, state and local postings with their closing dates and pay grades, helps you tailor a truthful resume to each announcement, and keeps track of what you sent. You review everything — nothing is submitted for you.",
    },
    search: {
      title: "Start where the announcements are",
      body:
        "Job Search opens on the government sector. Narrow to federal, state, or city and county, and every card shows the announcement's closing date so you can plan the application.",
    },
  },
};

export function tourStepsForMode(mode, steps = TOUR_STEPS) {
  const copy = MODE_TOUR_COPY[mode];
  if (!copy) return steps;
  return steps.map((s) => (copy[s.id] ? { ...s, ...copy[s.id] } : s));
}
